"use client";

import { Plug } from "lucide-react";
import {
  CrossSellBand,
  DevLandingShell,
  FinalCta,
  GetKeyButton,
  HeroBadge,
  LiveDot,
  SecondaryCta,
  SectionHeading,
  StatsRow,
  metalText,
} from "@/components/marketing/dev-landing/chrome";
import { FeatureSplit, TextFeatureList } from "@/components/marketing/dev-landing/sections";
import { GexEventsMock } from "@/components/marketing/dev-landing/mocks";
import { ChatDemo, Hi } from "@/components/marketing/dev-landing/chat-demo";
import {
  ClaudeConnectSteps,
  McpVideoPlaceholder,
  WorksWithRow,
} from "@/components/marketing/dev-landing/mcp-connect";
import { ToolCatalog } from "@/components/marketing/dev-landing/tool-catalog";

export default function GexMcpLandingClient({ keyHref }: { keyHref: string }) {
  return (
    <DevLandingShell>
      {/* Hero */}
      <div className="text-center">
        <HeroBadge>
          <LiveDot />
          <span className="text-sm text-[#d4d5d5]">Real-time GEX &middot; over MCP</span>
        </HeroBadge>

        <h1 className="mx-auto max-w-4xl text-balance text-4xl tracking-tight text-white sm:text-5xl md:text-6xl">
          Ask your AI where the <span style={metalText}>call wall</span> is
        </h1>

        <p className="mx-auto mt-6 max-w-2xl text-balance text-lg leading-relaxed text-white/70 sm:text-xl">
          Real-time gamma exposure as MCP tools: walls, the flip, heatmaps, vanna and market-wide GEX
          screening - answered live in Claude, ChatGPT or Cursor.
        </p>

        <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <GetKeyButton keyHref={keyHref} eventName="gex_mcp_lp_get_key_clicked" />
          <SecondaryCta href="#connect">
            <Plug className="h-4 w-4" />
            How to connect
          </SecondaryCta>
        </div>
        <p className="mt-4 text-xs text-white/50 sm:text-sm">Included with every paid QuantWheel plan.</p>

        <WorksWithRow />
      </div>

      {/* Demo video */}
      <McpVideoPlaceholder
        youtubeEmbedUrl="https://www.youtube-nocookie.com/embed/WHBlx9aTTXM?start=245"
        label="Watch Claude pull live SPY gamma walls, compare regimes across tickers and screen the market for GEX setups through the QuantWheel MCP connector."
      />

      <StatsRow
        stats={[
          { value: "6", label: "GEX tools" },
          { value: "Live", label: "full-chain compute" },
          { value: "<1min", label: "Claude setup" },
          { value: "All", label: "US optionable tickers" },
        ]}
      />

      {/* Chat demo */}
      <div className="mt-20 sm:mt-24">
        <SectionHeading
          title={
            <>
              Dealer positioning, <span style={metalText}>in conversation</span>
            </>
          }
          sub="No dashboards to tab through. Your AI queries the GEX engine directly and reasons over the levels with you."
        />
        <ChatDemo
          turns={[
            {
              question: "Is SPY in positive or negative gamma right now? Give me the key levels.",
              toolCalls: [{ tool: "get_gex", args: "SPY" }],
              answer: (
                <>
                  SPY is in <Hi>negative gamma</Hi> - spot 622.41 is above the flip at <Hi>620.5</Hi> but the
                  net profile flips negative below it. Call wall <Hi>630</Hi>, put wall <Hi>615</Hi>. Expect
                  dealer hedging to accelerate moves between 615 and 620, and fade strength into 630.
                </>
              ),
            },
            {
              question: "Which large caps flipped gamma regime this week?",
              toolCalls: [
                { tool: "get_gamma_flip_events", args: "7d" },
                { tool: "screen_gex_tickers", args: "large caps" },
              ],
              answer: (
                <>
                  Three names flipped: <Hi>NVDA</Hi> went negative on Tuesday and hasn&apos;t recovered,{" "}
                  <Hi>META</Hi> flipped positive after earnings, and <Hi>AMD</Hi> is oscillating around its
                  flip at 162.5 - the least stable setup of the three.
                </>
              ),
            },
          ]}
        />
      </div>

      {/* Connect to Claude */}
      <ClaudeConnectSteps toolsHint="the full GEX toolset - walls, flips, heatmaps, vanna and screening" />

      {/* Tool catalog */}
      <div className="mt-20 sm:mt-24">
        <SectionHeading
          title="The GEX toolset"
          sub="Six tools cover the whole dealer-positioning workflow - from one ticker's walls to a market-wide regime scan."
        />
        <ToolCatalog
          groups={[
            {
              title: "Levels & structure",
              description: "The numbers traders key off, recomputed live per request.",
              tools: [
                { name: "get_gex", blurb: "call wall, put wall, flip, per-strike GEX" },
                { name: "get_gex_heatmap", blurb: "net GEX by strike and expiration" },
                { name: "get_vanna_charm", blurb: "second-order Greeks exposure" },
                { name: "get_max_pain", blurb: "max pain per expiration" },
              ],
            },
            {
              title: "Regime & discovery",
              description: "Catch flips as they happen and find the next setup.",
              tools: [
                { name: "get_gamma_flip_events", blurb: "flip & wall-move events" },
                { name: "screen_gex_tickers", blurb: "market-wide GEX screening" },
                { name: "get_option_expirations", blurb: "expirations per symbol" },
                { name: "get_stock_quote", blurb: "live spot for context" },
              ],
            },
          ]}
        />
      </div>

      {/* Why */}
      <FeatureSplit
        eyebrow="Regime change, surfaced"
        title={
          <>
            &apos;What changed this week?&apos; gets a <span style={metalText}>factual answer</span>
          </>
        }
        lead="Flip and wall-move events give your AI a timeline of dealer positioning - so it reasons over what actually happened instead of guessing from training data."
        points={[
          { term: "get_gamma_flip_events", text: "every regime change, timestamped" },
          { term: "screen_gex_tickers", text: "market-wide scans for extreme positioning" },
          { term: "get_gex_heatmap", text: "0DTE pinning versus opex magnets, one call" },
        ]}
        visual={<GexEventsMock />}
      />

      <div className="mt-20 sm:mt-24">
        <SectionHeading
          title={
            <>
              Built for <span style={metalText}>gamma-aware</span> decisions
            </>
          }
        />
        <TextFeatureList
          items={[
            {
              title: "Walls that are current",
              description:
                "Levels are computed from the live options chain when the tool is called - not cached from this morning's open.",
            },
            {
              title: "Whole-surface answers",
              description:
                "Heatmap and vanna tools let your AI reason across expirations - 0DTE pinning versus opex magnets - in a single conversation.",
            },
          ]}
        />
      </div>

      <CrossSellBand
        badge="Prefer raw JSON?"
        title="The same GEX engine, as a REST API"
        description="Walls, flips, heatmaps and events as clean endpoints for your own code - same key, same data."
        href="/gex-api"
        linkLabel="Explore the GEX API"
      />

      <CrossSellBand
        className="mt-5"
        badge="Want everything?"
        title="The full MCP server: flows, screeners and your wheels"
        description="GEX is one of four tool surfaces - the overview page tours all 17 tools your connector unlocks."
        href="/mcp"
        linkLabel="See the full MCP"
      />

      <FinalCta
        title="Stop screenshotting GEX charts into chat"
        sub="Connect once and every conversation can pull live gamma walls for any ticker."
        keyHref={keyHref}
        eventName="gex_mcp_lp_get_key_clicked"
        note="Included with every paid QuantWheel plan."
      />
    </DevLandingShell>
  );
}
